import React, { useState } from 'react';

/* ── New natural palette ── */
const C = {
  bg: '#F4F3EF',
  bgSec: '#E8F0E9',
  card: '#EEF5EF',
  border: '#D4DDD6',
  primary: '#191919',
  surface: '#3F6F53',
  soft: '#8BBF9F',
  soil: '#8B6F47',
  texPri: '#2c2c2b',
  texSec: '#7d7a75',
};

const font = "'Manrope-Variable', -apple-system, system-ui, sans-serif";

/* ── Reusable card ── */
const Card = ({ children, style }) => (
  <div style={{
    background: C.card, border: `1px solid ${C.border}`, borderRadius: 16,
    padding: '18px 16px', ...style,
  }}>
    {children}
  </div>
);

/* ── Sample screens ── */
const GardenScreen = () => (
  <div>
    {/* Garden placeholder */}
    <div style={{
      height: 260, background: `linear-gradient(180deg, ${C.bgSec} 0%, ${C.bg} 100%)`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      position: 'relative',
    }}>
      <span style={{ fontSize: 64 }}>🌿🌳🌿</span>
      <div style={{
        position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)',
        background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(8px)',
        borderRadius: 20, padding: '8px 20px',
        display: 'flex', alignItems: 'center', gap: 8,
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
      }}>
        <span style={{ fontSize: 18 }}>🌱</span>
        <span style={{ fontFamily: font, fontSize: 13, fontWeight: 600, color: C.primary }}>
          6 piante cresciute
        </span>
      </div>
    </div>
    <div style={{ padding: '0 24px' }}>
      {/* Presence metric */}
      <div style={{ textAlign: 'center', padding: '28px 0 8px' }}>
        <div style={{ fontFamily: font, fontSize: 48, fontWeight: 700, color: C.texPri, lineHeight: 1 }}>
          22<span style={{ fontSize: 24, fontWeight: 500, color: C.texSec }}>/30</span>
        </div>
        <div style={{ fontFamily: font, fontSize: 14, fontWeight: 500, color: C.texSec, marginTop: 6 }}>
          giorni di presenza questo mese
        </div>
      </div>
      <div style={{ height: 4, borderRadius: 2, background: C.border, overflow: 'hidden', margin: '12px 0 36px' }}>
        <div style={{ height: '100%', borderRadius: 2, width: '73%', background: C.primary }} />
      </div>
      {/* Roadmap */}
      <div style={{ fontFamily: font, fontSize: 14, fontWeight: 600, color: C.texPri, marginBottom: 18 }}>
        Il tuo percorso
      </div>
      {[
        { phase: 'FASE 1', title: 'Piantare i semi', desc: 'Piccole azioni per tornare presente', active: true },
        { phase: 'FASE 2', title: 'Coltivare con cura', desc: 'Gesti pensati per i momenti più difficili' },
        { phase: 'FASE 3', title: 'Connessione profonda', desc: 'Il tuo giardino cresce con te' },
      ].map((p, i) => (
        <div key={i} style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{
              width: 44, height: 44, borderRadius: '50%',
              background: p.active ? C.primary : C.bgSec,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 20,
            }}>
              {p.active ? '🌱' : i === 2 ? '⭐' : '💚'}
            </div>
            {i < 2 && <div style={{ width: 2, height: 28, margin: '4px 0', background: C.border }} />}
          </div>
          <div style={{ paddingTop: 4, paddingBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontFamily: font, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', color: p.active ? C.primary : C.texSec }}>
                {p.phase}
              </span>
              {p.active && (
                <span style={{ fontFamily: font, fontSize: 9, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: C.bgSec, color: C.primary }}>
                  Ora
                </span>
              )}
            </div>
            <div style={{ fontFamily: font, fontSize: 15, fontWeight: 600, color: p.active ? C.texPri : C.texSec, marginTop: 3, opacity: p.active ? 1 : 0.5 }}>
              {p.title}
            </div>
            <div style={{ fontFamily: font, fontSize: 13, color: C.texSec, marginTop: 2, opacity: p.active ? 1 : 0.6 }}>
              {p.desc}
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const HomeScreen = () => (
  <div style={{ padding: '24px 24px 0' }}>
    {/* Header area */}
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
      <div>
        <div style={{ fontFamily: font, fontSize: 13, color: C.texSec }}>Buongiorno</div>
        <div style={{ fontFamily: font, fontSize: 22, fontWeight: 700, color: C.texPri }}>OGGI</div>
      </div>
      <div style={{ width: 40, height: 40, borderRadius: '50%', background: C.bgSec, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontSize: 18 }}>🔔</span>
      </div>
    </div>
    {/* Today's gesture card */}
    <Card style={{ marginBottom: 16 }}>
      <div style={{ fontFamily: font, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', color: C.surface, marginBottom: 8 }}>
        Gesto di oggi
      </div>
      <div style={{ fontFamily: font, fontSize: 17, fontWeight: 600, color: C.texPri, lineHeight: 1.4, marginBottom: 16 }}>
        Dedica cinque minuti a riordinare insieme prima di rilassarvi sul divano.
      </div>
      <button style={{
        width: '100%', padding: '14px 0', borderRadius: 10, border: 'none',
        background: C.primary, color: '#fff', fontFamily: font, fontSize: 15, fontWeight: 600, cursor: 'pointer',
      }}>
        Fatto
      </button>
      <div style={{ textAlign: 'center', marginTop: 12 }}>
        <span style={{ fontFamily: font, fontSize: 13, color: C.texSec }}>Non oggi</span>
      </div>
    </Card>
    {/* Micro-phrase */}
    <div style={{ textAlign: 'center', padding: '16px 0', fontFamily: font, fontSize: 14, fontStyle: 'italic', color: C.texSec }}>
      "Un piccolo gesto. Oggi basta questo."
    </div>
    {/* Streak indicator */}
    <Card>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ width: 48, height: 48, borderRadius: '50%', background: C.bgSec, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ fontSize: 22 }}>🔥</span>
        </div>
        <div>
          <div style={{ fontFamily: font, fontSize: 24, fontWeight: 700, color: C.texPri }}>6 giorni</div>
          <div style={{ fontFamily: font, fontSize: 13, color: C.texSec }}>di presenza consecutiva</div>
        </div>
      </div>
    </Card>
  </div>
);

const StatsScreen = () => (
  <div style={{ padding: '24px 24px 0' }}>
    <div style={{ textAlign: 'center', marginBottom: 24 }}>
      <div style={{ fontFamily: font, fontSize: 22, fontWeight: 700, color: C.texPri }}>Statistiche</div>
    </div>
    {/* Range selector */}
    <div style={{
      display: 'inline-flex', background: C.bgSec, borderRadius: 10, padding: 3, marginBottom: 20, width: '100%',
    }}>
      {['7gg', '30gg', '90gg'].map((r, i) => (
        <div key={r} style={{
          flex: 1, textAlign: 'center', padding: '8px 0', borderRadius: 8, fontFamily: font, fontSize: 13, fontWeight: 500,
          background: i === 1 ? '#fff' : 'transparent', color: i === 1 ? C.texPri : C.texSec, cursor: 'pointer',
        }}>
          {r}
        </div>
      ))}
    </div>
    {/* Stat cards 2x2 */}
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
      {[
        { label: 'Completati', value: '18' },
        { label: 'Serie attuale', value: '6' },
        { label: 'Tasso', value: '72%' },
        { label: 'Presenza 30gg', value: '22' },
      ].map(s => (
        <Card key={s.label}>
          <div style={{ fontFamily: font, fontSize: 11, fontWeight: 500, color: C.texSec, marginBottom: 10 }}>{s.label}</div>
          <div style={{ fontFamily: font, fontSize: 28, fontWeight: 700, color: C.texPri, lineHeight: 1 }}>{s.value}</div>
        </Card>
      ))}
    </div>
    {/* Weekly dots */}
    <Card>
      <div style={{ fontFamily: font, fontSize: 13, fontWeight: 500, color: C.texPri, marginBottom: 12 }}>Questa settimana</div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
        {['L', 'M', 'M', 'G', 'V', 'S', 'D'].map((d, i) => (
          <div key={i} style={{ textAlign: 'center' }}>
            <div style={{
              width: 32, height: 32, borderRadius: '50%', marginBottom: 4,
              background: i < 5 ? C.surface : i === 5 ? C.soft : C.border,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {i < 5 && <span style={{ color: '#fff', fontSize: 12 }}>✓</span>}
            </div>
            <span style={{ fontFamily: font, fontSize: 10, color: C.texSec }}>{d}</span>
          </div>
        ))}
      </div>
    </Card>
  </div>
);

const AchievementsScreen = () => (
  <div style={{ padding: '24px 24px 0' }}>
    <div style={{ textAlign: 'center', marginBottom: 24 }}>
      <div style={{ fontFamily: font, fontSize: 22, fontWeight: 700, color: C.texPri }}>Traguardi</div>
    </div>
    {[
      { emoji: '🌱', title: 'Primo seme', desc: 'Completa il tuo primo gesto', unlocked: true },
      { emoji: '🔥', title: '7 giorni', desc: 'Serie di 7 giorni consecutivi', unlocked: true },
      { emoji: '🌳', title: 'Radici profonde', desc: 'Completa 30 gesti', unlocked: false },
      { emoji: '💎', title: 'Diamante', desc: 'Serie di 30 giorni consecutivi', unlocked: false },
    ].map((a, i) => (
      <Card key={i} style={{ marginBottom: 12, opacity: a.unlocked ? 1 : 0.5 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{
            width: 52, height: 52, borderRadius: '50%', flexShrink: 0,
            background: a.unlocked ? C.bgSec : C.border,
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24,
          }}>
            {a.emoji}
          </div>
          <div>
            <div style={{ fontFamily: font, fontSize: 15, fontWeight: 600, color: C.texPri }}>{a.title}</div>
            <div style={{ fontFamily: font, fontSize: 13, color: C.texSec }}>{a.desc}</div>
          </div>
          {a.unlocked && <span style={{ marginLeft: 'auto', color: C.surface, fontSize: 18 }}>✓</span>}
        </div>
      </Card>
    ))}
  </div>
);

const EventsScreen = () => (
  <div style={{ padding: '24px 24px 0' }}>
    <div style={{ textAlign: 'center', marginBottom: 24 }}>
      <div style={{ fontFamily: font, fontSize: 22, fontWeight: 700, color: C.texPri }}>Eventi</div>
    </div>
    {[
      { icon: '🎂', title: 'Compleanno di Anna', date: '23 mar', color: C.soil },
      { icon: '💍', title: 'Anniversario', date: '15 apr', color: C.surface },
      { icon: '🌹', title: 'San Valentino', date: '14 feb', color: '#c45c5c' },
    ].map((e, i) => (
      <Card key={i} style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12, flexShrink: 0,
            background: C.bgSec,
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
          }}>
            {e.icon}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: font, fontSize: 15, fontWeight: 600, color: C.texPri }}>{e.title}</div>
            <div style={{ fontFamily: font, fontSize: 13, color: C.texSec }}>{e.date}</div>
          </div>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: e.color }} />
        </div>
      </Card>
    ))}
    <Card style={{ marginTop: 20, textAlign: 'center' }}>
      <span style={{ fontFamily: font, fontSize: 13, color: C.surface, fontWeight: 600, cursor: 'pointer' }}>
        + Aggiungi evento
      </span>
    </Card>
  </div>
);

/* ── Tab config ── */
const PAGES = [
  { id: 'garden', label: '🌳 Giardino', Component: GardenScreen },
  { id: 'home', label: '🏠 Home', Component: HomeScreen },
  { id: 'stats', label: '📊 Stats', Component: StatsScreen },
  { id: 'achievements', label: '🏆 Traguardi', Component: AchievementsScreen },
  { id: 'events', label: '📅 Eventi', Component: EventsScreen },
];

const PreviewV2Theme = () => {
  const [active, setActive] = useState('garden');
  const ActiveComponent = PAGES.find(p => p.id === active)?.Component || GardenScreen;

  return (
    <div style={{ background: C.bg, maxWidth: 430, margin: '0 auto' }}>
      {/* Tab bar */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 50,
        display: 'flex',
        background: C.bg,
        borderBottom: `1px solid ${C.border}`,
        padding: '12px 8px 0',
        overflowX: 'auto',
      }}>
        {PAGES.map(p => (
          <button
            key={p.id}
            onClick={() => setActive(p.id)}
            style={{
              flex: 1, padding: '8px 4px', fontSize: 12, fontFamily: font,
              fontWeight: active === p.id ? 600 : 400,
              color: active === p.id ? C.primary : C.texSec,
              background: 'transparent', border: 'none',
              borderBottom: active === p.id ? `2px solid ${C.primary}` : '2px solid transparent',
              cursor: 'pointer', whiteSpace: 'nowrap',
            }}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Color palette reference */}
      <div style={{ padding: '16px 24px 8px', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {[
          { c: C.bg, l: 'BG' }, { c: C.card, l: 'Card' }, { c: C.bgSec, l: 'Elevated' },
          { c: C.border, l: 'Border' }, { c: C.primary, l: 'Primary' },
          { c: C.surface, l: 'Surface' }, { c: C.soft, l: 'Soft' }, { c: C.soil, l: 'Soil' },
        ].map(s => (
          <div key={s.l} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{ width: 16, height: 16, borderRadius: 4, background: s.c, border: '1px solid rgba(0,0,0,0.1)' }} />
            <span style={{ fontFamily: font, fontSize: 10, color: C.texSec }}>{s.l}</span>
          </div>
        ))}
      </div>

      {/* Active page */}
      <ActiveComponent />

      <div style={{ height: 40 }} />
    </div>
  );
};

export default PreviewV2Theme;
